import { FC, useCallback, useMemo } from "react";
import { Prompt, useHistory } from "react-router-dom";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { isEmpty } from "lodash";
import __ from "src/utils/Translations";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesCompanyConfigurationWrite } from "src/utils/RoleBasedSecurity/permissions";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import { useListAbsenceTypes } from "src/Pages/Company/Absences/api/absenceTypes.queries";
import { useSaveCompanyConfigurations } from "src/Pages/Company/Absences/api/companyConfiguration.queries";
import { parseCompanyConfigurationToBackend } from "src/utils/Company/Absences/parseCompanyConfiguration";
import { BillingCycleConfigurationType } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import { IAbsencesConfigurationFormValues } from "./AbsencesConfiguration.models";

interface IAbsencesConfigurationFormProps {
  defaultValues: IAbsencesConfigurationFormValues;
}

const AbsencesConfigurationForm: FC<IAbsencesConfigurationFormProps> = ({
  defaultValues,
}) => {
  const companyId = useCompanyId();
  const history = useHistory();
  const logStatus = useLogStatus();
  const canWriteCompanyConfiguration = usePermission(
    absencesCompanyConfigurationWrite
  );

  if (!companyId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No company ID provided.",
      file: "AbsencesConfigurationForm.tsx",
    });
  }

  const { data: absenceTypesData } = useListAbsenceTypes(companyId);
  const companyConfigurationMutation = useSaveCompanyConfigurations(companyId);

  const mainAbsenceTypeOptions = useMemo(
    () =>
      absenceTypesData
        .filter(
          (absenceType) =>
            absenceType.active &&
            absenceType.billingCycleConfiguration?.type ===
              BillingCycleConfigurationType.Yearly &&
            !absenceType.billingCycleConfiguration?.parentAbsenceTypeId
        )
        .map((absenceType) => ({
          id: absenceType.id,
          label: absenceType.name,
        })),
    [absenceTypesData]
  );

  const formMethods = useForm<IAbsencesConfigurationFormValues>({
    defaultValues,
  });

  const {
    control,
    formState: { isDirty },
    handleSubmit,
  } = formMethods;

  const onSubmit = useCallback(
    (formData: IAbsencesConfigurationFormValues) => {
      const parsedFormData = parseCompanyConfigurationToBackend(formData);

      companyConfigurationMutation.mutate(parsedFormData);
    },
    [companyConfigurationMutation]
  );

  return (
    <FormProvider {...formMethods}>
      <form
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        data-testid="absences-configuration-form"
      >
        <Typography variant="h6" component="h3" sx={{ marginBottom: "1.5rem" }}>
          {__("Strona startowa modułu")}
        </Typography>
        <Typography variant="subtitle1" sx={{ marginBottom: "1rem" }}>
          {__("Ustawienie urlopu głównego")}
        </Typography>
        <Typography>
          {__(
            "Ustawienia widoku licznika w Panelu nieobecności. Wymiar wybranego szablonu będzie widoczny na stronie głównej pracownika."
          )}
        </Typography>
        <Controller
          name="mainAbsenceType"
          control={control}
          render={({
            field: { value, onChange, ref },
            fieldState: { invalid, error },
          }) => (
            <Autocomplete
              fullWidth
              options={mainAbsenceTypeOptions}
              value={value || null}
              disabled={!canWriteCompanyConfiguration}
              isOptionEqualToValue={(option, selectedValue) =>
                option.id === selectedValue?.id
              }
              getOptionLabel={(option) =>
                !isEmpty(option) ? option.label : ""
              }
              onChange={(_event, newValue) => {
                onChange(newValue);
              }}
              renderInput={(params: AutocompleteRenderInputParams) => (
                <TextField
                  {...params}
                  label={__("Szablon")}
                  error={invalid}
                  helperText={error?.message}
                  inputRef={ref}
                  data-testid="absences-configuration-form-field__mainAbsenceType"
                />
              )}
              sx={{ marginTop: "1.5rem" }}
            />
          )}
        />
        <Divider />
        <Typography variant="h6" component="h3" sx={{ marginBottom: "1.5rem" }}>
          {__("Powiadomienia mailowe")}
        </Typography>
        <Typography variant="subtitle1" sx={{ marginBottom: "1rem" }}>
          {__("Przypomnienie o wnioskach do rozpatrzenia")}
        </Typography>
        <Typography>
          {__(
            "Po włączeniu akceptant będzie otrzymywał maila z listą zaległych wniosków do akceptacji. Mail będzie wysyłany w każdy poniedziałek o godzinie 00:01."
          )}
        </Typography>
        <FormControl component="fieldset" variant="standard" margin="normal">
          <Controller
            name="waitingForApprovalRequestsReminder"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!value}
                    onChange={onChange}
                    disabled={!canWriteCompanyConfiguration}
                    data-testid="absences-configuration-form-field__waitingForApprovalRequestsReminder"
                  />
                }
                label={__("Włącz przypomnienie")}
                labelPlacement="end"
              />
            )}
          />
        </FormControl>
        <Divider />
        <Stack
          direction="row"
          sx={{ justifyContent: "flex-end", mt: "2rem" }}
          spacing={2}
        >
          <Button
            variant="outlined"
            type="button"
            size="large"
            sx={{ marginRight: "1rem" }}
            onClick={() => history.goBack()}
          >
            {__("Anuluj")}
          </Button>
          <Button
            variant="contained"
            type="submit"
            size="large"
            loading={companyConfigurationMutation.isPending}
            loadingPosition="start"
            disabled={!canWriteCompanyConfiguration}
            data-testid="absences-configuration-form__button-submit"
          >
            {__("Zapisz zmiany")}
          </Button>
        </Stack>
      </form>
      <Prompt
        when={isDirty && !companyConfigurationMutation.isSuccess}
        message={__("Niezapisane zmiany zostaną utracone.")}
      />
    </FormProvider>
  );
};

export default AbsencesConfigurationForm;
